import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  readFunctionalStorage,
  writeFunctionalStorage,
} from '@/utils/privacyPreferences'

export const ADMIN_TAB_QUERY_KEY = 'tab'

export type AdminTabQueryOptions<T extends string> = {
  tabs: readonly T[]
  defaultTab: T
  storageKey?: string
}

const readStoredTabIndex = (
  storageKey: string | undefined,
  tabCount: number
): number => {
  if (!storageKey || typeof window === 'undefined') {
    return 0
  }

  const storedValue = readFunctionalStorage(storageKey)
  if (storedValue === null) {
    return 0
  }

  const storedIndex = Number(storedValue)
  return Number.isInteger(storedIndex) &&
    storedIndex >= 0 &&
    storedIndex < tabCount
    ? storedIndex
    : 0
}

export const getAdminTabFromIndex = <T extends string>(
  tabs: readonly T[],
  index: number,
  defaultTab: T
): T => tabs[index] ?? defaultTab

export const getAdminTabIndex = <T extends string>(
  tabs: readonly T[],
  tab: T,
  defaultTab: T
): number => {
  const index = tabs.indexOf(tab)
  return index >= 0 ? index : tabs.indexOf(defaultTab)
}

export const resolveAdminTab = <T extends string>(
  tabs: readonly T[],
  urlValue: string | null,
  storedIndex: number,
  defaultTab: T
): T => {
  if (urlValue !== null) {
    return tabs.includes(urlValue as T) ? (urlValue as T) : defaultTab
  }

  return getAdminTabFromIndex(tabs, storedIndex, defaultTab)
}

export const buildAdminTabSearchParams = <T extends string>(
  searchParams: URLSearchParams,
  tab: T
): URLSearchParams => {
  const nextParams = new URLSearchParams(searchParams)
  nextParams.set(ADMIN_TAB_QUERY_KEY, tab)
  return nextParams
}

export const useAdminTabQuery = <T extends string>({
  tabs,
  defaultTab,
  storageKey,
}: AdminTabQueryOptions<T>) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [storedIndex, setStoredIndex] = useState(() =>
    readStoredTabIndex(storageKey, tabs.length)
  )

  useEffect(() => {
    setStoredIndex(readStoredTabIndex(storageKey, tabs.length))
  }, [storageKey, tabs.length])

  const urlTab = searchParams.get(ADMIN_TAB_QUERY_KEY)
  const activeTab = resolveAdminTab(tabs, urlTab, storedIndex, defaultTab)
  const activeTabIndex = getAdminTabIndex(tabs, activeTab, defaultTab)

  useEffect(() => {
    if (urlTab === null || tabs.includes(urlTab as T)) {
      return
    }

    setSearchParams(buildAdminTabSearchParams(searchParams, defaultTab), {
      replace: true,
    })
  }, [defaultTab, searchParams, setSearchParams, tabs, urlTab])

  const handleTabChange = (index: number) => {
    const nextTab = getAdminTabFromIndex(tabs, index, defaultTab)
    const nextIndex = getAdminTabIndex(tabs, nextTab, defaultTab)
    setStoredIndex(nextIndex)
    setSearchParams(buildAdminTabSearchParams(searchParams, nextTab))

    if (storageKey) {
      writeFunctionalStorage(storageKey, String(nextIndex))
    }
  }

  return {
    activeTab,
    activeTabIndex,
    handleTabChange,
  }
}
